import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "../../Helpers/Pagination";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from "recharts";

const CustomerReport = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [overallOutstanding, setOverallOutstanding] = useState(0); // Machan, idhu mottha outstanding-kaga

  /* ================= FILTER STATE ================= */
  const [today, setToday] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  /* ================= PAGINATION ================= */
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  /* ================= LOAD OVERALL OUTSTANDING (NO LIMIT) ================= */
  const loadOverallStats = async () => {
    // Machan, inga limit/page parameters illa, so mottha data-vum varum
    const res = await Handler({
      method: "get",
      url: `/reports/customer_report.php?org_id=${user.org_id}&all=1` 
    });

    if (res?.success && res.data?.rows) {
      const total = res.data.rows.reduce((acc, curr) => acc + Number(curr.outstanding || 0), 0);
      setOverallOutstanding(total);
    }
  };

  /* ================= LOAD PAGINATED REPORT ================= */
  const loadReport = async (p = page) => {
    dispatch(loadingTrue());

    const params = new URLSearchParams();
    params.append("org_id", user.org_id);
    params.append("page", p);
    params.append("limit", limit);

    if (today) params.append("today", 1);
    if (dateFrom) params.append("date_from", dateFrom.toISOString().slice(0, 10));
    if (dateTo) params.append("date_to", dateTo.toISOString().slice(0, 10));

    const res = await Handler({
      method: "get",
      url: `/reports/customer_report.php?${params.toString()}`
    });

    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg || "Failed");
      return;
    }

    setRows(res.data?.rows || []);
    setTotals(res.data?.totals || null);
    setTotalRows(res.data?.pagination?.total_rows || 0);
    setPage(p);
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (user?.org_id) {
      loadReport(1);
      loadOverallStats(); // Machan, overall data-vayum fetch panrom
    }
  }, [user?.org_id]);

  /* ================= AUTO FILTER ================= */
  useEffect(() => {
    loadReport(1);
  }, [today, dateFrom, dateTo]);

  /* ================= HANDLERS ================= */
  const handleToday = () => {
    setToday(!today);
    setDateFrom(null);
    setDateTo(null);
  };

  const handleDateFrom = (d) => {
    setToday(false);
    setDateFrom(d);
  };

  const handleDateTo = (d) => {
    setToday(false);
    setDateTo(d);
  };

  const handlePageChange = (p) => {
    loadReport(p);
  };

  /* ================= CHART DATA ================= */
  const barData = rows.map(r => ({
    name: r.name,
    Collections: r.collections,
    Outstanding: r.outstanding > 0 ? r.outstanding : 0
  }));

  const pieData = [
    { name: "Collections", value: totals?.collections || 0 },
    { name: "Outstanding", value: overallOutstanding || 0 }
  ];

  return (
    <>
      {/* ================= FILTER BAR ================= */}
      <div className="filter-bar mb-4">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Button
              color={today ? "primary" : "outline-primary"}
              onClick={handleToday}
            >
              Today
            </Button>
          </Col>

          <Col md={3} className="gap-2 d-flex align-items-center">
            <label className="small mb-0">Date From</label>
            <DatePicker
              selected={dateFrom}
              onChange={handleDateFrom}
              dateFormat="dd-MM-yyyy"
              className="form-control"
            />
          </Col>

          <Col md={3} className="gap-2 d-flex align-items-center">
            <label className="small mb-0">Date To</label>
            <DatePicker
              selected={dateTo}
              onChange={handleDateTo}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              minDate={dateFrom}
            />
          </Col>
        </Row>
      </div>

      {/* ================= SUMMARY ================= */}
      {totals && (
        <Row className="mb-4 g-3">
          <Col md={3}><div className="kpii-card kpii-blue"><div className="kpii-label">Customers</div><div className="kpii-value">{totals.customers}</div></div></Col>
          <Col md={3}><div className="kpii-card kpii-purple"><div className="kpii-label">Bills</div><div className="kpii-value">{totals.bills}</div></div></Col>
          <Col md={3}><div className="kpii-card kpii-green"><div className="kpii-label">Collections</div><div className="kpii-value">₹ {Number(totals.collections).toFixed(2)}</div></div></Col>
          
          {/* Machan, inga ippo Overall Outstanding kaatum (across all pages) */}
          <Col md={3}>
            <div className="kpii-card kpii-red">
              <div className="kpii-label">Total Outstanding</div>
              <div className="kpii-value">₹ {overallOutstanding.toFixed(2)}</div>
            </div>
          </Col>
        </Row>
      )}

      {/* ================= CHARTS ================= */}
      <Row className="mb-4 g-3">
        <Col md={8}>
          <div className="chart-card" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="Collections" fill="#16a34a" />
                <Bar dataKey="Outstanding" fill="#dc2626" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>

        <Col md={4}>
          <div className="chart-card" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" innerRadius={70} outerRadius={110}>
                  <Cell fill="#22c55e" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <div className="billing-table-card">
        <div className="table-responsive">
          <table className="billing-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Outlet</th>
                <th className="right">Bills</th>
                <th className="right">Refunds</th>
                <th className="right">Collections</th>
                <th className="right">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {rows && rows?.map(r => (
                <tr key={r.id}>
                  <td>
                    <div className="cust-name">{r.name}</div>
                    <div className="cust-phone">{r.phone}</div>
                  </td>
                  <td>{r.outlet_name}</td>
                  <td className="right">{r.bills}</td>
                  <td className="right">
                    ₹ {Number(r.refunds || 0).toFixed(2)}
                  </td>

                  <td className="right success">₹ {Number(r.collections || 0).toFixed(2)}</td>
                  <td className={`right ${r.outstanding > 0 ? "danger" : "muted"}`}>
                    ₹ {Number(r.outstanding).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          limit={limit}
          total={totalRows}
          onChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default CustomerReport;